import { ApiError, onearlyRequest } from '@echo/api-client/client';
import { OnEarlyRequestError } from './errors';
import type { CompleteExerciseInput } from './outbox';
import { performAsFor, type WriteScope } from './scope';

/**
 * A handwriting photo still on the device, waiting for Core: where the file is and what it
 * is. On the phone `uri` is a `file://` copy the app owns; a browser hands over a `blob:` url.
 */
export interface PendingPhoto {
  uri: string;
  name: string;
  mimeType: string;
}

/** The photo a reader was asked for is not on the device any more; it will not come back. */
export class MissingPhotoError extends Error {
  constructor(name: string) {
    super(`The photo ${name} is no longer on the device`);
    this.name = 'MissingPhotoError';
  }
}

/** Reads a pending photo into a Blob the platform's `fetch` can put in a multipart body. */
export type PhotoReader = (photo: PendingPhoto) => Promise<Blob>;

// Browsers hand over `blob:` / `data:` urls, which fetch reads back. The phone installs its own
// reader (expo-file-system's File): Expo's fetch has no support for React Native's `{ uri }` part.
let readPhoto: PhotoReader = async (photo) => (await fetch(photo.uri)).blob();

export function configurePhotoReader(reader: PhotoReader): void {
  readPhoto = reader;
}

/**
 * What an upload is for, in `POST /api/upload/file`'s query: the lineage the file joins, and the
 * group a member writes in. Core stores the bytes where that content lives, so the write that
 * attaches them finds them there.
 */
export interface UploadTarget {
  lineageUuid?: string;
  groupUuid?: string;
}

/**
 * The photo is for an attempt that doesn't exist yet, which joins the day's session in `scope`.
 * As a member, the group (the results are the group's, at its home) and the session once there
 * is one, which Core checks the member may add to. As the person, their own session, at their
 * home, where an upload that names nothing lands anyway.
 */
export function photoUploadTarget(scope: WriteScope, sessionLineageUuid?: string): UploadTarget {
  const session = sessionLineageUuid ? { lineageUuid: sessionLineageUuid } : {};
  return scope.kind === 'member' ? { ...session, groupUuid: scope.groupActorUuid } : session;
}

function uploadPath(target: UploadTarget): string {
  const query = new URLSearchParams();
  if (target.lineageUuid) query.append('lineageUuid', target.lineageUuid);
  if (target.groupUuid) query.append('groupUuid', target.groupUuid);
  const search = query.toString();
  return search ? `/api/upload/file?${search}` : '/api/upload/file';
}

/**
 * `POST /api/upload/file` — multipart, field `file`, up to 32 MB. The endpoint is not in
 * Core's OpenAPI contract (so nothing is generated for it); the reply is the uuid of the
 * File content node, which the writing attempt then CONTAINS (docs/mvp/03-CONTENT.md).
 * `target` goes in the query, where Core reads it without reading the file; a Core that doesn't
 * know `lineageUuid` ignores it, and `groupUuid` already places a group's upload at its home.
 */
export async function uploadPhoto(photo: PendingPhoto, target: UploadTarget = {}): Promise<string> {
  const body = new FormData();
  body.append('file', await readPhoto(photo), photo.name);
  const response = await onearlyRequest<{ data?: { fileUuid?: string }; status: number }>(
    uploadPath(target),
    { method: 'POST', body },
  );
  const fileUuid = response.data?.fileUuid;
  if (!fileUuid) {
    throw new OnEarlyRequestError(
      `OnEarly: uploading the photo returned no file (${response.status})`,
      response.status,
    );
  }
  return fileUuid;
}

/** The types Core's profile image route takes (`ALLOWED_IMAGE_MIME_TYPES`); anything else is a 415. */
const PROFILE_PHOTO_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]);

/** The chosen image is in a format Core refuses as a profile photo, such as HEIC. */
export class UnsupportedPhotoFormatError extends Error {
  constructor(mimeType: string) {
    super(`OnEarly: a profile photo cannot be ${mimeType || 'an untyped file'}`);
    this.name = 'UnsupportedPhotoFormatError';
  }
}

/**
 * `POST /api/upload/user-profile-image` — multipart, field `image`, ≤ 10 MB, JPEG / PNG /
 * WebP / GIF. Core resizes it into variants and sets it as the person's OnEarly profile
 * photo (`profileImage` on `GET /users/me`), so every OnEarly product sees the new one.
 * The OnEarly app uses the same route; there is no REST profile field for the image.
 */
export async function uploadProfilePhoto(photo: PendingPhoto): Promise<void> {
  if (!PROFILE_PHOTO_TYPES.has(photo.mimeType.toLowerCase())) {
    throw new UnsupportedPhotoFormatError(photo.mimeType);
  }
  const body = new FormData();
  body.append('image', await readPhoto(photo), photo.name);
  const response = await onearlyRequest<{
    status: number;
    data?: { profileImageContentUuid?: string };
  }>('/api/upload/user-profile-image', { method: 'POST', body }).catch((error: unknown) => {
    // A file the browser could not type goes up as application/octet-stream.
    if (error instanceof ApiError && error.status === 415) {
      throw new UnsupportedPhotoFormatError(photo.mimeType);
    }
    throw error;
  });
  if (!response.data?.profileImageContentUuid) {
    throw new OnEarlyRequestError(
      `OnEarly: updating the profile photo returned nothing (${response.status})`,
      response.status,
    );
  }
}

/**
 * The upload only stores bytes (`files.content_uuid` stays empty); `mutateContent.addFiles`
 * is what turns them into a File content node — the uuid the attempt's CONTAINS edge and
 * `GET /api/download/file/{uuid}` want. GraphQL, like the share (`share.ts`): there is no
 * REST route for it. The subject is the person, so the node is theirs to read. Written in the
 * attempt's own scope: Core lets an attempt CONTAIN only what its author can read.
 */
export async function createPhotoNode(
  fileUuid: string,
  options: { subjectUuid?: string; scope: WriteScope },
): Promise<string> {
  const performAs = performAsFor(options.scope);
  const { subjectUuid } = options;
  const response = await onearlyRequest<{
    status: number;
    data?: {
      errors?: Array<{ message: string }>;
      data?: {
        mutateContent?: {
          mutationMetadata?: { clientIdMap?: Array<{ clientId: string; uuid: string }> };
        };
      };
    };
  }>('/graphql', {
    method: 'POST',
    body: JSON.stringify({
      query:
        'mutation AttachPhoto($input: MutateContentInput!) { mutateContent(input: $input) { mutationMetadata { clientIdMap { clientId uuid } } } }',
      variables: {
        input: {
          addFiles: [{ clientId: 'photo', fileUuid, ...(subjectUuid ? { subjectUuid } : {}) }],
          ...(performAs ? { performAs } : {}),
        },
      },
    }),
  });
  const error = response.data?.errors?.[0]?.message;
  // A GraphQL error answers 200: the request itself is what Core refuses, so no retry.
  if (error) throw new OnEarlyRequestError(`OnEarly: attaching the photo failed — ${error}`, 400);
  const uuid = response.data?.data?.mutateContent?.mutationMetadata?.clientIdMap?.find(
    (mapping) => mapping.clientId === 'photo',
  )?.uuid;
  if (!uuid) {
    throw new OnEarlyRequestError(
      `OnEarly: attaching the photo returned no node (${response.status})`,
      response.status,
    );
  }
  return uuid;
}

/**
 * Swap the photo on the device for its Core uuid: upload what is still local, keep what
 * already went through (an earlier try may have uploaded and then failed to save the attempt).
 * The scope it was written in stays with it, so a retry writes the attempt in the same one.
 * `sessionLineageUuid` is the day's session in that scope, when it already exists.
 * A photo gone from the device is dropped: the attempt is saved without it rather than
 * waiting for a file that cannot return.
 */
export async function ensurePhotoUploaded(
  input: CompleteExerciseInput,
  options: { subjectUuid?: string; scope: WriteScope; sessionLineageUuid?: string },
): Promise<CompleteExerciseInput> {
  if (!input.photo) return input;
  const { photo: _pending, ...rest } = input;
  let fileUuid: string;
  try {
    fileUuid = await uploadPhoto(
      input.photo,
      photoUploadTarget(options.scope, options.sessionLineageUuid),
    );
  } catch (error) {
    if (error instanceof MissingPhotoError) return { ...rest, scope: options.scope };
    throw error;
  }
  const photoFileUuid = await createPhotoNode(fileUuid, options);
  return { ...rest, photoFileUuid, scope: options.scope };
}
